import { resolveConfidenceVisitorId } from './confidenceVisitorId'

export const FACEBOOK_CLICK_ID_KEY = 'fbc'
const AGE_KEY = 'age'
const FACEBOOK_PIXEL_ID_OVERRIDE_KEY = 'fbPixelIdOverride'

/**
 * Resolves the Facebook Pixel/dataset id to use for the current session.
 *
 * Defaults to the app-wide pixel id, but a specific funnel can opt into a
 * dedicated dataset by setting an override (see setFacebookPixelIdOverride).
 * This keeps behavior unchanged for every funnel that never sets an override.
 */
export function getFacebookPixelId(defaultPixelId: string): string {
  try {
    return (
      localStorage.getItem(FACEBOOK_PIXEL_ID_OVERRIDE_KEY) || defaultPixelId
    )
  } catch {
    return defaultPixelId
  }
}

export function setFacebookPixelIdOverride(pixelId: string) {
  localStorage.setItem(FACEBOOK_PIXEL_ID_OVERRIDE_KEY, pixelId)
}

export function clearFacebookPixelIdOverride() {
  localStorage.removeItem(FACEBOOK_PIXEL_ID_OVERRIDE_KEY)
}

export function addAgeToLocalStorage(age: string) {
  localStorage.setItem(AGE_KEY, age)
}

export function getAgeFromLocalStorage(): string | undefined {
  return localStorage.getItem(AGE_KEY) ?? undefined
}

/**
 * The Confidence visitor id, or `"unknown"` where one cannot exist.
 *
 * A thin wrapper so every caller — page view tracking, signup metadata and the
 * identity merge — reads the same value that was handed to the Confidence SDK,
 * and so no caller has to know how it is resolved or persisted.
 *
 * The earlier sessionStorage cache is gone. It existed because the first page
 * view of a session could fire before the SDK wrote its cookie, but it could
 * not help once the cookie was evicted mid-session, which is the failure that
 * actually cost us coverage. resolveConfidenceVisitorId holds the id in module
 * memory instead, which covers both.
 *
 * `"unknown"` is kept as the absent value because callers already treat it as
 * such, and the database merge functions explicitly reject it.
 */
export function getConfidenceVisitorId(): string {
  return resolveConfidenceVisitorId() ?? 'unknown'
}

export function addFacebookParamsToLocalStorage(locationSearch: string) {
  const urlSearchParams = new URLSearchParams(locationSearch)

  addFbcToLocalStorage(urlSearchParams)
  addOtherFacebookParamsToLocalStorage(urlSearchParams)
}

function addFbcToLocalStorage(urlSearchParams: URLSearchParams) {
  const fbclid = urlSearchParams.get('fbclid')

  if (fbclid) {
    const creationTime = Date.now()
    const subdomainIndex = import.meta.env.MODE === 'development' ? 0 : 1

    // NOTE: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/#2--format-clickid
    const fbc = `fb.${subdomainIndex}.${creationTime}.${fbclid}`

    localStorage.setItem(FACEBOOK_CLICK_ID_KEY, fbc)
  }
}

function addOtherFacebookParamsToLocalStorage(
  urlSearchParams: URLSearchParams,
) {
  const adParams = ['vm_c', 'vm_co', 'vm_m', 'vm_s', 'vm_t']

  adParams.forEach((param) => {
    const value = urlSearchParams.get(param)

    if (value) localStorage.setItem(param, value)
  })
}
